// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the target's side of Bump (bump-plan.md sections 1 and 3). The records come from GET /users/@me/bumps
// (pulled; no gateway event of their own), fetched:
// - on a manual MESSAGE_ACK this client didn't send (a bump that moved the read state);
// - at READY and RESUMED (the shared reconnect list);
// - when a channel opens unread and has no known record: auto-ack is held for that channel until the fetch ends
//   (3 s at most), so the read state doesn't pass the boundary first.
// The banner shows for the open channel when it has a record, and stays until the user leaves the chat by any
// route (useBumpChatOnScreen), which consumes the record (DELETE), so returning later shows nothing. Fetches are
// collected for 100 ms, one at a time.
// Exclude from upstream sync.

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {registerReconnectHandler} from '@app/features/gateway/events/ReconnectHandlers';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeAutoObservable, observable, runInAction} from 'mobx';

export interface BumpRecord {
	channel_id: string;
	bumped_by: string;
	at: string;
	boundary_message_id: string;
}

export const BUMPS_PATH = '/users/@me/bumps';
export const BUMP_FETCH_COLLECT_MS = 100;
export const BUMP_ACK_HOLD_MAX_MS = 3000;
export const OWN_MANUAL_ACK_TTL_MS = 30_000;
export const BUMP_BUSY_AFTER_MS = 2000;

const logger = new Logger('ChannelBumps');

class ChannelBumps {
	private readonly records = observable.map<string, BumpRecord>();
	/** The open channel's banner, kept until the switch even if the record goes. */
	banner: BumpRecord | null = null;
	private openChannelId: string | null = null;
	private readonly holds = observable.set<string>();
	private readonly busy = observable.set<string>();
	/** Channel id to expiry (epoch ms) of a manual ack this client sent. */
	private readonly ownManualAcks = new Map<string, number>();
	/** Channel id to the `at` of the record whose banner was shown (not shown again, even if fetched again). */
	private readonly dismissed = new Map<string, string>();
	private collectTimer: ReturnType<typeof setTimeout> | null = null;
	private inFlight: Promise<void> | null = null;
	private again = false;
	private waiters: Array<() => void> = [];
	private generation = 0;

	constructor() {
		makeAutoObservable<this, 'collectTimer' | 'inFlight' | 'waiters' | 'ownManualAcks' | 'dismissed' | 'generation'>(
			this,
			{collectTimer: false, inFlight: false, waiters: false, ownManualAcks: false, dismissed: false, generation: false},
			{autoBind: true},
		);
	}

	recordFor(channelId: string): BumpRecord | undefined {
		return this.records.get(channelId);
	}

	bannerFor(channelId: string): BumpRecord | null {
		return this.banner?.channel_id === channelId ? this.banner : null;
	}

	isHoldingAck(channelId: string): boolean {
		return this.holds.has(channelId);
	}

	isBusy(channelId: string): boolean {
		return this.busy.has(channelId);
	}

	setBusy(channelId: string, busy: boolean): void {
		if (busy) this.busy.add(channelId);
		else this.busy.delete(channelId);
	}

	/** ReadStates.sendManualAck: this client's own "Mark as unread", not a bump. */
	noteOwnManualAck(channelId: string): void {
		this.ownManualAcks.set(channelId, Date.now() + OWN_MANUAL_ACK_TTL_MS);
	}

	handleMessageAck(channelId: string, manual: boolean): void {
		if (!manual) return;
		const own = this.ownManualAcks.get(channelId);
		if (own != null) {
			this.ownManualAcks.delete(channelId);
			if (own > Date.now()) return;
		}
		void this.requestFetch();
	}

	handleReady(): void {
		void this.requestFetch();
	}

	handleReconnect(): void {
		void this.requestFetch();
	}

	handleChannelOpened(channelId: string, unread: boolean): void {
		this.openChannelId = channelId;
		if (this.showBannerIfRecord(channelId) || !unread) return;
		this.holds.add(channelId);
		const release = () => runInAction(() => this.holds.delete(channelId));
		const timeout = setTimeout(release, BUMP_ACK_HOLD_MAX_MS);
		void this.requestFetch().finally(() => {
			clearTimeout(timeout);
			release();
		});
	}

	handleChannelLeft(channelId: string): void {
		this.holds.delete(channelId);
		if (this.openChannelId === channelId) this.openChannelId = null;
		const shown = this.bannerFor(channelId);
		if (shown == null) return;
		this.banner = null;
		this.dismissed.set(channelId, shown.at);
		this.records.delete(channelId);
		void http.delete(`${BUMPS_PATH}/${channelId}`).catch((error: unknown) => {
			logger.warn('Consuming a bump record failed:', error);
		});
	}

	/** Collected for 100 ms; one request at a time; resolves once a fetch that started after the call has ended. */
	requestFetch(): Promise<void> {
		const done = new Promise<void>((resolve) => this.waiters.push(resolve));
		if (this.inFlight != null) {
			this.again = true;
		} else if (this.collectTimer == null) {
			this.collectTimer = setTimeout(() => {
				this.collectTimer = null;
				void this.runFetch();
			}, BUMP_FETCH_COLLECT_MS);
		}
		return done;
	}

	private async runFetch(): Promise<void> {
		const generation = this.generation;
		const waiters = this.waiters;
		this.waiters = [];
		this.again = false;
		const fetch = (async () => {
			try {
				const response = await http.get<Array<BumpRecord>>(BUMPS_PATH);
				if (generation === this.generation) this.applyRecords(response.body ?? []);
			} catch (error) {
				logger.warn('Fetching bump records failed:', error);
			}
		})();
		this.inFlight = fetch;
		await fetch;
		for (const resolve of waiters) resolve();
		// A reset meanwhile (logout) started over: this fetch no longer owns the queue.
		if (generation !== this.generation) return;
		this.inFlight = null;
		if (this.again) void this.runFetch();
	}

	private applyRecords(records: Array<BumpRecord>): void {
		this.records.clear();
		for (const record of records) {
			if (this.dismissed.get(record.channel_id) === record.at) continue;
			this.records.set(record.channel_id, record);
		}
		if (this.openChannelId != null) this.showBannerIfRecord(this.openChannelId);
	}

	private showBannerIfRecord(channelId: string): boolean {
		if (this.bannerFor(channelId) != null) return true;
		const record = this.records.get(channelId);
		if (record == null) return false;
		this.banner = record;
		return true;
	}

	reset(): void {
		this.generation++;
		if (this.collectTimer != null) clearTimeout(this.collectTimer);
		this.collectTimer = null;
		this.inFlight = null;
		this.again = false;
		for (const resolve of this.waiters) resolve();
		this.waiters = [];
		this.records.clear();
		this.banner = null;
		this.openChannelId = null;
		this.holds.clear();
		this.busy.clear();
		this.ownManualAcks.clear();
		this.dismissed.clear();
	}
}

const channelBumps = new ChannelBumps();
ResettableStates.register(channelBumps, channelBumps.reset);
// RESUMED: the records again, through the fork's reconnect list (favorites-bookmarks-plan.md section 10).
registerReconnectHandler(() => channelBumps.handleReconnect());
export default channelBumps;
